import React, { useState, useEffect } from 'react';
import { Copy, Check, User, Sparkles } from 'lucide-react';
import { copyToClipboard } from '../../utils/helpers';

/**
 * Chat Message Component
 * 
 * Displays user or AI message bubble with optional typewriter effect
 */
const ChatMessage = ({
  role,
  content,
  timestamp,
  showCopy = true,
  isNew = false,
  className = '',
}) => {
  const [copied, setCopied] = useState(false);
  const [displayedContent, setDisplayedContent] = useState(isNew && role === 'assistant' ? '' : content);
  const [isTyping, setIsTyping] = useState(isNew && role === 'assistant');
  const isUser = role === 'user';

  // Typewriter effect for new AI messages
  useEffect(() => {
    if (!isNew || isUser || !content) return;

    let currentIndex = 0;
    const typingSpeed = 10; // ms per character
    const chunkSize = 3; // Characters per tick for faster typing

    const typeText = () => {
      if (currentIndex < content.length) {
        const nextIndex = Math.min(currentIndex + chunkSize, content.length);
        setDisplayedContent(content.slice(0, nextIndex));
        currentIndex = nextIndex;
        setTimeout(typeText, typingSpeed);
      } else {
        setIsTyping(false);
      }
    };

    typeText();
  }, [content, isNew, isUser]);

  const handleCopy = async () => {
    const success = await copyToClipboard(content);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Format content with line breaks and basic formatting
  const formatContent = (text) => {
    if (!text) return null;
    
    // Split by lines
    const lines = text.split('\n');
    
    return lines.map((line, i) => {
      // Check for emoji headers (like 📦 Materials)
      if (/^[📦💰📋💡🧱📏🎨🏠🪵🏗️🔧✓✗⚠️]/.test(line.trim())) {
        return (
          <div key={i} className="font-semibold mt-3 mb-1 first:mt-0 text-gray-900">
            {line}
          </div>
        );
      }
      
      // Check for bullet points
      if (line.trim().startsWith('•') || line.trim().startsWith('-') || line.trim().startsWith('*')) {
        return (
          <div key={i} className="ml-4 py-0.5">
            {line}
          </div>
        );
      }
      
      // Check for numbered items
      if (/^\d+\./.test(line.trim())) {
        return (
          <div key={i} className="ml-2 py-0.5">
            {line}
          </div>
        );
      }
      
      // Empty line
      if (line.trim() === '') {
        return <div key={i} className="h-2" />;
      }
      
      return <div key={i}>{line}</div>;
    });
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} ${className} animate-slide-up`}>
      <div className={`flex gap-3 max-w-[85%] ${isUser ? 'flex-row-reverse' : ''}`}>
        {/* Avatar */}
        <div className={`
          w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center
          ${isUser 
            ? 'bg-gradient-to-br from-blue-600 to-indigo-600' 
            : 'bg-gradient-to-br from-gray-100 to-gray-200'
          }
        `}>
          {isUser ? (
            <User className="w-4 h-4 text-white" />
          ) : (
            <Sparkles className="w-4 h-4 text-blue-600" />
          )}
        </div>

        {/* Message bubble */}
        <div className={`
          relative group rounded-2xl px-4 py-3
          ${isUser 
            ? 'bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-tr-sm shadow-lg shadow-blue-500/20' 
            : 'bg-white border border-gray-200 text-gray-800 rounded-tl-sm shadow-sm hover:shadow-md transition-shadow'
          }
        `}>
          <div className={`text-sm whitespace-pre-wrap ${isUser ? '' : 'space-y-1'}`}>
            {isUser ? content : formatContent(displayedContent)}
            {isTyping && (
              <span className="inline-block w-2 h-4 ml-1 bg-blue-500 animate-pulse rounded-sm"></span>
            )}
          </div>
          
          {/* Copy button for AI messages */}
          {!isUser && showCopy && !isTyping && (
            <button
              onClick={handleCopy}
              className="absolute top-2 right-2 p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 opacity-0 group-hover:opacity-100 transition-all duration-200"
              title="Copy to clipboard"
            >
              {copied ? (
                <Check className="w-4 h-4 text-green-600" />
              ) : (
                <Copy className="w-4 h-4 text-gray-500" />
              )}
            </button>
          )}
          
          {/* Timestamp */}
          {timestamp && !isTyping && (
            <div className={`text-xs mt-2 ${isUser ? 'text-blue-200' : 'text-gray-400'}`}>
              {new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Typing Indicator Component
 * Shows animated dots when AI is thinking
 */
export const TypingIndicator = () => {
  return (
    <div className="flex justify-start animate-fade-in">
      <div className="flex gap-3">
        <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
          <Sparkles className="w-4 h-4 text-blue-600" />
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
          <div className="flex gap-1.5 items-center">
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;
